import {
  Box,
  ChakraTheme,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs as ChakraTabs,
  TabsProps,
} from '@chakra-ui/react';
import { useState } from 'react';
import { Typography } from 'Tokens';

export const TabsTheme: ChakraTheme['components']['Tabs'] = {
  sizes: {
    md: {
      tab: {
        px: '0px',
        py: '14px',
      },
    },
  },
  variants: {
    line: {
      tablist: {
        borderBottom: '1px',
        borderBottomColor: 'border.decorative',
        paddingBottom: '1px',
      },
      tab: {
        color: 'text.muted',
        _hover: {
          bg: 'bg.hover',
          borderBottomColor: 'bg.hover',
          _selected: {
            borderBottom: '2px',
          },
        },
        _selected: {
          borderBottom: '2px',
          bg: 'transparent',
          borderBottomColor: 'text.selected',
          color: 'border.selected.accent',
          _hover: {
            bg: 'bg.hover',
            cursor: 'auto',
          },
        },
      },
    },
  },
};

export const TABS_VARIANTS = Object.keys(TabsTheme.variants ?? {});
export type TabsVariant = (typeof TABS_VARIANTS)[number];

export type TabItemProps = {
  title: string | React.ReactNode;
  id: string;
  leftElement?: React.ReactNode;
  rightElement?: React.ReactNode;
  content: React.ReactNode;
  isDisabled?: boolean;
  isHidden?: boolean;
  disabledTooltip?: string;
};
export const Tabs = ({
  items,
  currentTab,
  onChange,
  padList,
  tabsGap,
  tabPadding,
  ...props
}: Omit<TabsProps, 'variant' | 'children'> & {
  variant?: TabsVariant;
  items: TabItemProps[];
  currentTab?: string;
  onChange?: (id: string) => void;
  padList?: TabsProps['padding'];
  tabsGap?: TabsProps['gap'];
  tabPadding?: TabsProps['padding'];
}) => {
  const [internalCurrentTab, setInternalCurrentTab] = useState<number>(0);
  return (
    <ChakraTabs
      index={currentTab ? (items.findIndex((x) => x.id === currentTab) ?? 0) : internalCurrentTab}
      onChange={onChange ? (num) => onChange?.(items[num].id) : (num) => setInternalCurrentTab(num)}
      isLazy
      width="100%"
      lazyBehavior="unmount"
      {...props}
      display="flex"
      flexDirection="column"
      flexGrow="1"
    >
      <TabList paddingX={padList ?? '0px'} gap={tabsGap ?? '20px'}>
        {items
          .filter((item) => !item.isHidden)
          .map((item) => (
            <Tab key={item.id} isDisabled={item.isDisabled} padding={tabPadding}>
              {item?.leftElement && (
                <Box paddingRight="4px" fontSize="16px">
                  {item.leftElement}
                </Box>
              )}
              <Typography variant="bodyStrong" color="currentColor">
                {item.title}
              </Typography>
              {item?.rightElement && <Box paddingLeft="4px">{item.rightElement}</Box>}
            </Tab>
          ))}
      </TabList>
      <TabPanels width="100%" display="flex" flexGrow="1">
        {items
          .filter((item) => !item.isHidden)
          .map((item) => (
            <TabPanel key={item.id} padding="0px" width="100%" display="flex" flexGrow="1">
              {item.content}
            </TabPanel>
          ))}
      </TabPanels>
    </ChakraTabs>
  );
};
